import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, RefreshCw, Eye, History, ChevronLeft, ChevronRight, Package, MapPin, User, DollarSign, Truck, AlertTriangle, CheckCircle, Clock, X, QrCode, Printer, FileText } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import QRCode from 'qrcode';
import Barcode from 'react-barcode';

const TrackByVoucher = () => {
  const [searchParams] = useSearchParams();
  const [voucherNo, setVoucherNo] = useState('');
  const [vouchers, setVouchers] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    initializeData();
  }, []);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (warning) {
      const timer = setTimeout(() => {
        setWarning('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [warning]);

  useEffect(() => {
    if (currentOrder && currentOrder.VoucherNo) {
      generateQRCode(currentOrder.VoucherNo);
    }
  }, [currentOrder]);

  const initializeData = () => {
    setVoucherNo('');
    setVouchers([]);
    setCurrentPage(0);
    setCurrentOrder(null);
    setStatusHistory([]);
    setOrderHistory([]);
    setIsLoading(false);
    setQrCodeUrl('');
  };

  const handleTrack = async (overrideVoucherNo) => {
    // Button onClick passes a SyntheticEvent — only treat real string/number overrides as input.
    const raw =
      typeof overrideVoucherNo === 'string' || typeof overrideVoucherNo === 'number'
        ? overrideVoucherNo
        : voucherNo;
    const inputValue = String(raw ?? '').trim();
    if (!inputValue || inputValue === '[object Object]') {
      setWarning('Please enter voucher numbers');
      return;
    }

    const voucherNumbers = inputValue
      .split('.')
      .map((v) => v.trim())
      .filter((v) => v !== '');
    
    if (voucherNumbers.length === 0) {
      setWarning('Please enter valid voucher numbers');
      return;
    }

    try {
      setIsLoading(true);
      setStatusHistory([]);
      setOrderHistory([]);
      setError('');
      setSuccess('');
      setWarning('');

      const data = {
        VoucherNos: voucherNumbers,
      };

      const response = await MainService.TrackOrdersByVoucherNo(data);
      
      if (response && response.length > 0) {
        setVouchers(response);
        setCurrentPage(0);
        setCurrentOrder(response[0]);
        setSuccess(`Found ${response.length} order(s)`);
      } else {
        setVouchers([]);
        setCurrentOrder(null);
        setWarning('No orders found');
      }
    } catch (error) {
      console.error('Error tracking orders:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-track when opened from top-menu Quick Track (?voucher=...)
  useEffect(() => {
    const voucher = searchParams.get('voucher');
    if (!voucher?.trim()) return;
    setVoucherNo(voucher);
    void handleTrack(voucher);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTrack();
    }
  };

  const handleNext = () => {
    if (currentPage < vouchers.length - 1) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      setCurrentOrder(vouchers[newPage]);
      setStatusHistory([]);
      setOrderHistory([]);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      setCurrentOrder(vouchers[newPage]);
      setStatusHistory([]);
      setOrderHistory([]);
    }
  };

  const showOrderStatus = async (orderId) => {
    try {
      setIsLoading(true);
      const response = await MainService.OrderStatusByOrderId(orderId);
      setStatusHistory(response || []);
      setShowStatusModal(true);
    } catch (error) {
      console.error('Error loading order status:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const showOrderHistory = async (orderId) => {
    try {
      setIsLoading(true);
      const response = await MainService.OrderHistoryByOrderId(orderId);
      setOrderHistory(response || []);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error loading order history:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!currentOrder?.Id) {
      setWarning('No order selected to print');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const response = await MainService.PrintOrder(currentOrder.Id);
      const filePath = typeof response === 'string' ? response : (response?.data || response);

      if (filePath) {
        const fullUrl = String(filePath).startsWith('http')
          ? filePath
          : `${getApiBaseUrl()}ReportFiles/${filePath}`;
        window.open(fullUrl, '_blank');
      } else {
        setError('Print error: No file path returned');
      }
    } catch (err) {
      console.error('Print error:', err);
      setError('Print error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'cancelled':
        return <X className="w-5 h-5 text-red-500" />;
      default:
        return <Truck className="w-5 h-5 text-blue-500" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const generateQRCode = async (text) => {
    try {
      const url = await QRCode.toDataURL(text, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(url);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  if (isLoading && vouchers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-primary">Searching orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {warning && (
        <div className="fixed top-20 right-4 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{warning}</span>
          <button onClick={() => setWarning('')} className="text-yellow-500 hover:text-yellow-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <FileText className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Track By Voucher</h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">Enter voucher numbers to track your orders</p>
        </div>

        {/* Search Section */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Voucher Numbers
            </label>
            <input
              type="text"
              value={voucherNo}
              onChange={(e) => setVoucherNo(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter Voucher Numbers Ex: 101.150.554.6658 etc"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex items-end gap-3">
            <button
              onClick={() => handleTrack()}
              disabled={isLoading}
              className="bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
            >
              <Search className="w-4 h-4" />
              <span>Track</span>
            </button>
            <button
              onClick={initializeData}
              className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {vouchers.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Pagination Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <h2 className="text-xl font-semibold text-gray-900">
                  Order {currentPage + 1} of {vouchers.length}
                </h2>
                {currentOrder && (
                  <>
                    <button
                      onClick={() => showOrderStatus(currentOrder.Id)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Status</span>
                    </button>
                    <button
                      onClick={() => showOrderHistory(currentOrder.Id)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                    >
                      <History className="w-4 h-4" />
                      <span>History</span>
                    </button>
                    <button
                      onClick={handlePrint}
                      disabled={isLoading}
                      className="bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                    >
                      <Printer className="w-4 h-4" />
                      <span>Print</span>
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevious}
                  disabled={currentPage === 0}
                  className="bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentPage === vouchers.length - 1}
                  className="bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Order Details */}
          {currentOrder && (
            <div className="p-6">
              {/* Four Top Sections */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
                {/* Voucher QR Code and Barcode */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-2xl font-bold text-green-600">{currentOrder.VoucherNo || 'N/A'}</p>
                    </div>
                    <div>
                      <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 text-center">
                        {qrCodeUrl ? (
                          <img 
                            src={qrCodeUrl} 
                            alt={`QR Code for ${currentOrder.VoucherNo}`}
                            className="w-20 h-20 mx-auto"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-gray-100 rounded-lg mx-auto mb-2 flex items-center justify-center">
                            <QrCode className="w-10 h-10 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="bg-white p-3 rounded-lg border">
                        <div className="flex justify-center">
                          <Barcode
                            value={currentOrder.VoucherNo || 'N/A'}
                            format="CODE128"
                            width={1.5}
                            height={40}
                            displayValue={true}
                            fontSize={12}
                            margin={0}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Info */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Package className="w-5 h-5 mr-2 text-orange-500" />
                    Order Info
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(currentOrder.Status?.Name)}
                      <span className="text-xl font-bold text-green-600">{currentOrder.Status?.Name || 'N/A'}</span>
                    </div>
                    <div className="space-y-2">
                      <p><span className="font-medium">Client Bill No:</span> {currentOrder.ClientBillNo || 'N/A'}</p>
                      <p><span className="font-medium">Order Date:</span> {formatDate(currentOrder.Date)}</p>
                      <p><span className="font-medium">Pickup Date:</span> {formatDate(currentOrder.PickupDate)}</p>
                      <p><span className="font-medium">Delivery Date:</span> {formatDate(currentOrder.DeliveryDate)}</p>
                    </div>
                  </div>
                </div>

                {/* Shipper Session */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-green-500" />
                    Shipper 
                  </h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {currentOrder.Client?.Name || 'N/A'}</p>
                    <p><span className="font-medium">Mobile 1:</span> {currentOrder.Client?.MobileNo1 || 'N/A'}</p>
                    <p><span className="font-medium">Mobile 2:</span> {currentOrder.Client?.MobileNo2 || 'N/A'}</p>
                  </div>
                </div>

                {/* Pickup Address */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-blue-500" />
                    Pickup Address
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="font-medium text-gray-700">Address:</span> <span className="text-gray-900">{currentOrder.ClientAddress?.Address || 'N/A'}</span></p>
                    <p className="text-sm"><span className="font-medium text-gray-700">PO Box:</span> <span className="text-gray-900">{currentOrder.ClientAddress?.POBOX || 'N/A'}</span></p>
                    <p className="text-sm"><span className="font-medium text-gray-700">Location:</span> <span className="text-gray-900">{currentOrder.ClientAddress?.Location?.Name || 'N/A'}</span></p>
                    <p className="text-sm"><span className="font-medium text-gray-700">Emirate:</span> <span className="text-gray-900">{currentOrder.ClientAddress?.Emirate?.Name || 'N/A'}</span></p>
                  </div>
                </div>
              </div>

              {/* Four Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                {/* Customer */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-green-500" />
                    Customer
                  </h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {currentOrder.Customer?.Name || 'N/A'}</p>
                    <p><span className="font-medium">Mobile 1:</span> {currentOrder.Customer?.MobileNo1 || 'N/A'}</p>
                    <p><span className="font-medium">Mobile 2:</span> {currentOrder.Customer?.MobileNo2 || 'N/A'}</p>
                  </div>
                </div>

                {/* Delivery Address */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Truck className="w-5 h-5 mr-2 text-purple-500" />
                    Delivery Address
                  </h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Address:</span> {currentOrder.CustomerAddress?.Address || 'N/A'}</p>
                    <p><span className="font-medium">PO Box:</span> {currentOrder.CustomerAddress?.POBOX || 'N/A'}</p>
                    <p><span className="font-medium">Location:</span> {currentOrder.CustomerAddress?.Location?.Name || 'N/A'}</p>
                    <p><span className="font-medium">Emirate:</span> {currentOrder.CustomerAddress?.Emirate?.Name || 'N/A'}</p>
                  </div>
                </div>

                {/* Payment Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-green-500" />
                    Payment Info
                  </h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Product Price:</span> {currentOrder.ProductPrice || 0} AED</p>
                    <p><span className="font-medium">Delivery Charge:</span> {currentOrder.DeliveryCharge || 0} AED</p>
                    <p><span className="font-medium">Tax:</span> {currentOrder.TAX || 0} AED</p>
                    <p className="font-medium text-gray-700">Collection Amount: {currentOrder.CustomerAmount || 0} AED</p>
                    <p className="font-medium text-gray-700">Client Amount: {currentOrder.ClientAmount || 0} AED</p>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Truck className="w-5 h-5 mr-2 text-indigo-500" />
                    Delivery Info
                  </h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Pay Type:</span> {currentOrder.PayType || 'N/A'}</p>
                    <p><span className="font-medium">Payment Status:</span> {currentOrder.PaymentStatus || 'N/A'}</p>
                    <p><span className="font-medium">Return Status:</span> {currentOrder.ReturnStatus || 'N/A'}</p>
                    <p><span className="font-medium">Attempts:</span> {currentOrder.Attempts || 0}</p>
                    <p><span className="font-medium">Remarks:</span> {currentOrder.Reason?.Remarks || 'N/A'}</p>
                    <p><span className="font-medium">Reason:</span> {currentOrder.Reason?.Description || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Message Box when no orders */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Track Your Orders</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Enter voucher numbers separated by dots (e.g., 101.150.554.6658) to track your orders and view detailed information.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">How to track:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Enter one or more voucher numbers</li>
                  <li>• Separate multiple numbers with dots</li>
                  <li>• Click Track or press Enter</li>
                  <li>• View detailed order information</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">What you'll see:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Order status and tracking info</li>
                  <li>• Pickup and delivery addresses</li>
                  <li>• Customer and payment details</li>
                  <li>• Complete order history</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">Order Status Timeline</h3>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              <div className="relative">
                <div className="space-y-6">
                  {statusHistory.map((status, index) => (
                    <div key={index} className="relative flex items-start">
                      {index !== statusHistory.length - 1 && (
                        <div className="absolute left-4 top-8 w-0.5 h-16 bg-gray-200"></div>
                      )}
                      <div className="relative z-10 flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">
                              {status.Status?.Name || 'N/A'}
                            </h4>
                            <span className="text-sm text-gray-500">
                              {formatDateTime(status.Date)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(status.Status?.Name)}
                            <span className="text-sm text-gray-600">Status updated</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {statusHistory.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">No status history available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">Order Activity Timeline</h3>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              <div className="relative">
                <div className="space-y-6">
                  {orderHistory.map((history, index) => (
                    <div key={index} className="relative flex items-start">
                      {index !== orderHistory.length - 1 && (
                        <div className="absolute left-4 top-8 w-0.5 h-16 bg-gray-200"></div>
                      )}
                      <div className="relative z-10 flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">
                              {history.User?.UserName || 'System'}
                            </h4>
                            <span className="text-sm text-gray-500">
                              {formatDateTime(history.Date)}
                            </span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <History className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-gray-700">
                              {history.Remarks || 'No description available'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {orderHistory.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <History className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">No activity history available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackByVoucher;
